import React, { useState, useRef, useEffect } from 'react';

function SearchableSelect({ options, value, onChange, placeholder, displayField = 'name', subDisplayField = null }) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const containerRef = useRef(null);
  const inputRef = useRef(null);
  const dropdownRef = useRef(null);

  const selectedOption = options.find(opt => opt.id === value);

  useEffect(() => {
    if (selectedOption) {
      setSearchTerm('');
    }
  }, [selectedOption]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const filteredOptions = options.filter(opt =>
    opt[displayField]?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (subDisplayField && opt[subDisplayField]?.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const handleInputChange = (e) => {
    setSearchTerm(e.target.value);
    setIsOpen(true);
    setSelectedIndex(-1);
  };

  const handleInputFocus = () => {
    setIsOpen(true);
  };

  const handleClear = () => {
    onChange('');
    setSearchTerm('');
    setIsOpen(false);
    inputRef.current?.focus();
  };

  const handleOptionClick = (option) => {
    onChange(option.id);
    setSearchTerm('');
    setIsOpen(false);
    inputRef.current?.blur();
  };

  const handleKeyDown = (e) => {
    if (!isOpen) {
      if (e.key === 'ArrowDown' || e.key === 'Enter') {
        setIsOpen(true);
        e.preventDefault();
      }
      return;
    }

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex(prev => (prev < filteredOptions.length - 1 ? prev + 1 : prev));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex(prev => (prev > 0 ? prev - 1 : 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (selectedIndex >= 0 && selectedIndex < filteredOptions.length) {
        handleOptionClick(filteredOptions[selectedIndex]);
      }
    } else if (e.key === 'Escape') {
      setIsOpen(false);
    }
  };

  const getDisplayValue = () => {
    if (searchTerm !== '') {
      return searchTerm;
    }
    if (selectedOption) {
      const mainValue = selectedOption[displayField];
      const subValue = subDisplayField ? selectedOption[subDisplayField] : null;
      return subValue ? `${mainValue} (${subValue})` : mainValue;
    }
    return '';
  };

  return (
    <div className="searchable-select" ref={containerRef} style={{ position: 'relative' }}>
      <div style={{ position: 'relative' }}>
        <input
          ref={inputRef}
          type="text"
          className="form-control"
          value={getDisplayValue()}
          onChange={handleInputChange}
          onFocus={handleInputFocus}
          onKeyDown={handleKeyDown}
          placeholder={placeholder || 'Search...'}
          autoComplete="off"
          style={{ paddingRight: selectedOption ? '30px' : '12px' }}
        />
        {selectedOption && (
          <button
            type="button"
            onClick={handleClear}
            style={{
              position: 'absolute',
              right: '8px',
              top: '50%',
              transform: 'translateY(-50%)',
              border: 'none',
              background: 'none',
              cursor: 'pointer',
              fontSize: '18px',
              color: '#666',
              padding: '0',
              lineHeight: '1'
            }}
            title="Clear selection"
          >
            ×
          </button>
        )}
      </div>
      {isOpen && filteredOptions.length > 0 && (
        <div
          ref={dropdownRef}
          className="searchable-select-dropdown"
          style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            width: '100%',
            backgroundColor: 'white',
            border: '1px solid #ddd',
            borderRadius: '4px',
            maxHeight: '200px',
            overflowY: 'auto',
            zIndex: 1000,
            marginTop: '4px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
          }}
        >
          {filteredOptions.map((option, index) => (
            <div
              key={option.id}
              className={`searchable-select-option ${index === selectedIndex ? 'selected' : ''}`}
              onClick={() => handleOptionClick(option)}
              style={{
                padding: '8px 12px',
                cursor: 'pointer',
                backgroundColor: index === selectedIndex ? '#f0f0f0' : 'transparent',
                color: '#333',
                '&:hover': {
                  backgroundColor: '#f0f0f0'
                }
              }}
              onMouseEnter={() => setSelectedIndex(index)}
            >
              <div style={{ fontWeight: index === selectedIndex ? '500' : '400', color: '#333' }}>
                {option[displayField]}
              </div>
              {subDisplayField && option[subDisplayField] && (
                <div style={{ fontSize: '12px', color: '#666' }}>
                  {option[subDisplayField]}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
      {isOpen && filteredOptions.length === 0 && searchTerm && (
        <div
          style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            width: '100%',
            backgroundColor: 'white',
            border: '1px solid #ddd',
            borderRadius: '4px',
            padding: '8px 12px',
            zIndex: 1000,
            marginTop: '4px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
            color: '#333'
          }}
        >
          No results found
        </div>
      )}
    </div>
  );
}

export default SearchableSelect;
